import _ from 'lodash';

export enum MessagingAuthorType {
    SUPPORT = 'SUPPORT',
    PROVIDER = 'PROVIDER',
    PATIENT = 'PATIENT',
}

export enum MessagingOrderEnum {
    ASCENDING = 'ASCENDING',
    DESCENDING = 'DESCENDING',
}

export enum MessagingSortType {
    CREATED = 'CREATED',
    UPDATED = 'UPDATED',
    OPEN = 'OPEN',
    READ = 'READ',
    SUBJECT = 'SUBJECT',
    AUTHOR = 'AUTHOR',
}

export enum MessagingTagType {
    USER = 'USER',
    SYSTEM = 'SYSTEM',
}

export interface IMessagingAuthor {
    id?: string;
    nickname?: string;
    type?: keyof typeof MessagingAuthorType;
}

export interface IMessagingTag {
    id?: string;
    name?: string;
    type?: keyof typeof MessagingTagType;
    groupId?: string;
    created?: string;
}

export interface IMessagingMessage {
    id?: string;
    subject?: string;
    comments?: number;
    documents?: number;
    open?: boolean;
    read?: boolean;
    author?: IMessagingAuthor;
    lastAuthor?: IMessagingAuthor;
    tags?: IMessagingTag[];
    updated?: string;
    created?: string;
    claimPatientFirst?: string;
    claimPatientLast?: string;
    claimGuarantorFirst?: string;
    claimGuarantorLast?: string;
    claimFromDos?: string;
    claimThroughDos?: string;
    claim?: string;
    from?: string;
    purposeType?: string;
    priority?: string;
}

export interface IMessagingComment {
    id?: string;
    body?: string;
    note?: boolean;
    documents?: string[];
    author?: IMessagingAuthor;
    created?: string;
}

export const authorNickname = (message: IMessagingMessage | IMessagingComment | undefined): string => {
    return getNickname(_.get(message, 'author'));
};

export const getNickname = (author: IMessagingAuthor | undefined): string => {
    let nickname = _.get(author, 'nickname');
    const type = _.get(author, 'type');

    if (nickname === 'Business Account' && type === MessagingAuthorType.SUPPORT) {
        return 'Enter Support';
    }

    if (nickname && nickname !== 'Enter, Inc.' && type === MessagingAuthorType.SUPPORT) {
        const pair = _.split(nickname, ' ');
        const firstName = _.first(pair);
        const lastInitial = _.head(_.last(pair));
        nickname = `${firstName} ${lastInitial}.`;
    }
    return nickname || '';
};

export const isLastAuthor = (message: IMessagingMessage, author: IMessagingAuthor): boolean => {
    return _.get(message, 'lastAuthor.id') === _.get(author, 'id');
};

export const sortMessages = (
    messages: IMessagingMessage[],
    sorts: MessagingSortType[] = [],
    orders: MessagingOrderEnum[] = [],
): IMessagingMessage[] => {
    // sort results
    let sortCriteria: string[] = _.map(sorts, (sort: MessagingSortType) => _.toLower(sort));
    if (_.isEmpty(sortCriteria)) {
        sortCriteria.push('updated');
    }
    // NB: extra magic for sorting by author
    sortCriteria = _.map(sortCriteria, sort => (sort === 'author' ? 'author.nickname' : sort));

    // NB: expect both provider/support enums to have 'ASCENDING'
    const orderCriteria = _.map(orders, order => (String(order) === 'ASCENDING' ? 'asc' : 'desc'));
    if (_.isEmpty(orderCriteria)) {
        orderCriteria.push('desc');
    }
    return _.orderBy(messages, sortCriteria, orderCriteria);
};

export const isHtmlContent = (text: string): boolean => {
    if (!text) {
        return false;
    }
    if (isMarkdownContent(text)) return false;
    // Prefer a real DOM parser when running in the browser
    if (typeof window !== 'undefined' && typeof (window as any).DOMParser !== 'undefined') {
        try {
            const parser = new DOMParser();
            const doc = parser.parseFromString(text, 'text/html');
            const body = doc.body;

            if (!body) {
                return false;
            }

            // If the parser only produced a single text node that matches the original text,
            // then this is not considered HTML (likely plain text or markdown).
            if (body.childNodes.length === 1 && body.textContent === text) {
                return false;
            }

            // Treat it as HTML if there is at least one element node in the parsed body.
            return Array.from(body.childNodes).some(node => node.nodeType === Node.ELEMENT_NODE);
        } catch {
            // Fall through to non‑parser heuristic below if DOMParser fails
        }
    }

    // Fallback: very conservative check for angle‑bracket tags in non‑browser environments.
    const maybeTagPattern = /<[^>]+>/;
    return maybeTagPattern.test(text);
};

export const normalizeMarkdown = (text: string): string => {
    if (!text) return text;
    
    return text.replace(/```(\w*)\s*([^`]+?)```/g, (match, lang, content) => {
        const trimmedContent = content.trim();
        if (!trimmedContent) return match;
        return `\`\`\`${lang}\n${trimmedContent}\n\`\`\``;
    });
};

export const isMarkdownContent = (text: string): boolean => {
    if (!text) {
        return false;
    }
    const isMarkdownAutolink = /<\s*(https?:\/\/|mailto:)[^>]+>/i.test(text);
    if (isMarkdownAutolink) return true;
    // Common markdown patterns:
    const markdownPatterns: RegExp[] = [
        /\[.+?\]\(.+?\)/,              // links: [text](url)
        /^#{1,6}\s.+/m,               // headings: #, ##, ...
        /^(\s*[-*+]\s+).+/m,          // bullet lists
        /^\s*\d+\.\s+.*/m,            // ordered lists
        /```[\s\S]*?```/,             // fenced code blocks
        /`[^`]+`/,                    // inline code
        /(^|\s)[*_]{1,2}[^*_]+[*_]{1,2}(\s|$)/, // *italic* / **bold**
        /^>\s+.*/m,                   // blockquotes
    ];

    return markdownPatterns.some(re => re.test(text));
};

export const sanitizeHtml = (html: string): string => {
    let sanitized = html
        .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
        .replace(/on\w+\s*=\s*["'][^"']*["']/gi, '')
        .replace(/on\w+\s*=\s*[^\s>]*/gi, '');
    
    // Extract body content if full HTML document is provided
    const bodyMatch = sanitized.match(/<body[^>]*>([\s\S]*)<\/body>/i);
    if (bodyMatch) {
        sanitized = bodyMatch[1];
    }
    
    // Remove remaining structural HTML tags
    sanitized = sanitized
        .replace(/<\/?html[^>]*>/gi, '')
        .replace(/<\/?head[^>]*>/gi, '')
        .replace(/<\/?body[^>]*>/gi, '');
    
    return sanitized.trim();
};
